import { appContext } from "../../../../AppContext";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { JSX } from "react";
import Helper from "../../../../Common/Helper";
import { IAdvancedSecurityEntity } from "./DataModels";
import NoneIcon from '../../../../Assets/Images/securityRight/None.png';
import BasicIcon from '../../../../Assets/Images/securityRight/Basic.png';
import LocalIcon from '../../../../Assets/Images/securityRight/Local.png';
import DeepIcon from '../../../../Assets/Images/securityRight/Deep.png';
import GlobalIcon from '../../../../Assets/Images/securityRight/Global.png';
import { PagedResponse } from "@iconics/core";
import "./Styles.scss";
import { SerendieSymbol } from "@serendie/symbols";
import { Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse } from "@iconics/components";

export default class DataPermissions extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }

    private cirlceClassNames = new Map<number, string>([
        [0, "circle-icon circle-none-icon"],
        [1, "circle-icon circle-quarter-icon"],
        [2, "circle-icon circle-half-icon"],
        [3, "circle-icon circle-three-quarter-icon"],
        [4, "circle-icon circle-full-icon"],
    ]);

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            gridData: { rows: [] },
            gridRefreshToken: '',
        }
    }

    private async handleRowsRequest(request: IGridRowsRequest<IEntityPermissions>): Promise<IGridRowsResponse<IEntityPermissions>>
    {
        const response = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.roleDataSecurity.get<PagedResponse<IAdvancedSecurityEntity[]>>({
                id: this.props.roleId,
                tenancy: appContext().tenantId,
                skipToken: request.skipToken,
                orderBy: DataGrid.toODataOrderBy(request.sorter),
                top: request.take,
        });
        const permissions = response.value.map(x => ({
            id: x.Id,
            recursiveNodes: x.RecursiveNodes,
            eventTypeId: x.EventTypeId,
            eventTypeName: x.EventType.Name,
            eventTypeDisplayName: x.EventType.DisplayName,
            create: x.Create,
            read: x.Read,
            update: x.Update,
            delete: x.Delete,
            lock: x.Lock,
            unlock: x.Unlock,
            append: x.Append,
            appendTo: x.AppendTo,
            share: x.Share,
        } as IEntityPermissions))

        return { rows: permissions, nextSkipToken: response.skipToken };
    }

    private getPermission(permissions: IEntityPermissions, name: PermissionName): number
    {
        switch (name)
        {
            case 'create':
                return permissions.create;
            case 'read':
                return permissions.read;
            case 'update':
                return permissions.update;
            case 'delete':
                return permissions.delete;
            case 'lock':
                return permissions.lock;
            case 'unlock':
                return permissions.unlock;
            case 'share':
                return permissions.share;
            default:
                throw new Error('Not supported');
        }
    }

    private setPermission(permissions: IEntityPermissions, name: PermissionName, value: number)
    {
        switch (name)
        {
            case 'create':
                permissions.create = value;
                break;
            case 'read':
                permissions.read = value;
                break;
            case 'update':
                permissions.update = value;
                break;
            case 'delete':
                permissions.delete = value;
                break;
            case 'lock':
                permissions.lock = value;
                break;
            case 'unlock':
                permissions.unlock = value;
                break;
            case 'share':
                permissions.share = value;
                break;
            default:
                throw new Error('Not supported');
        }
    }

    private async onPermissionIncrement(permissions: IEntityPermissions, permissionName: PermissionName): Promise<void>
    {
        if (!this.props.canUpdateRole)
        {
            return;
        }

        const permissionValue = this.getPermission(permissions, permissionName);
        const newPermissionValue = (permissionValue + 1) % 5;
        this.setPermission(permissions, permissionName, newPermissionValue);

        try
        {
            await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.roleDataSecurity.put({
                id: this.props.roleId,
                tenancy: appContext().tenantId,
                body: {
                    Id: permissions.id,
                    Append: 0,
                    AppendTo: 0,
                    Create: permissions.create,
                    Read: permissions.read,
                    Update: permissions.update,
                    Delete: permissions.delete,
                    Lock: permissions.lock,
                    Unlock: permissions.unlock,
                    Share: permissions.share,
                }
            });
            // instead of resetting the table we simply do this to re-render it without refreshing
            this.setState({ gridData: this.state.gridData });
        }
        catch (error)
        {
            console.log(error);
        }
    }

    public render(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_Role_DataSecurityDetailsTitle}
                subTitle={this.labels.Platform_Role_DataSecurityDetailsSubTitle}
                icon={<SerendieSymbol name='apps-circle' />}
            >
                <GridContainer>
                    <GridItem
                        style={{
                            display: 'flex',
                            flexDirection: 'column-reverse',
                        }}
                    >
                        <div className="securityIcons">
                            <div className="securityIcon-item">
                                <p className="securityIcon-title">{this.labels.Platform_Role_Security_Level0}</p>
                                <img className="securityIcon-image" src={NoneIcon} alt="NoneIcon"></img>
                            </div>
                            <div className="securityIcon-item">
                                <p className="securityIcon-title">{this.labels.Platform_Role_Security_Level1}</p>
                                <img className="securityIcon-image" src={BasicIcon} alt="BasicIcon"></img>
                            </div>
                            <div className="securityIcon-item" >
                                <p className="securityIcon-title">{this.labels.Platform_Role_Security_Level2}</p>
                                <img className="securityIcon-image" src={LocalIcon} alt="LocalIcon"></img>
                            </div>
                            <div className="securityIcon-item" >
                                <p className="securityIcon-title">{this.labels.Platform_Role_Security_Level3}</p>
                                <img className="securityIcon-image" src={DeepIcon} alt="DeepIcon"></img>
                            </div>
                            <div className="securityIcon-item" >
                                <p className="securityIcon-title">{this.labels.Platform_Role_Security_Level4}</p>
                                <img className="securityIcon-image" src={GlobalIcon} alt="GlobalIcon"></img>
                            </div>
                        </div>
                    </GridItem>
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: `${Helper.nameOf<IAdvancedSecurityEntity>("EventType")}/Name`,
                                    title: this.labels.Entity_Name,
                                    getCellValue: i => i.eventTypeName,
                                    columnWidth: 22,
                                    sortable: true,
                                },
                                {
                                    columnId: `${Helper.nameOf<IAdvancedSecurityEntity>("EventType")}/DisplayName`,
                                    title: this.labels.Entity_DisplayName,
                                    getCellValue: i => i.eventTypeDisplayName,
                                    columnWidth: 25,
                                    sortable: true,
                                },
                                {
                                    columnId: Helper.nameOf<IAdvancedSecurityEntity>("Create"),
                                    title: this.labels.Platform_Role_Security_Create,
                                    getCellValue: i => this.renderPermissionCell(i, "create"),
                                    columnWidth: 9,
                                },
                                {
                                    columnId: Helper.nameOf<IAdvancedSecurityEntity>("Read"),
                                    title: this.labels.Platform_Role_Security_Read,
                                    getCellValue: i => this.renderPermissionCell(i, "read"),
                                    columnWidth: 9,
                                },
                                {
                                    columnId: Helper.nameOf<IAdvancedSecurityEntity>("Update"),
                                    title: this.labels.Platform_Role_Security_Update,
                                    getCellValue: i => this.renderPermissionCell(i, "update"),
                                    columnWidth: 9,
                                },
                                {
                                    columnId: Helper.nameOf<IAdvancedSecurityEntity>("Delete"),
                                    title: this.labels.Platform_Role_Security_Delete,
                                    getCellValue: i => this.renderPermissionCell(i, "delete"),
                                    columnWidth: 9,
                                },
                                {
                                    columnId: Helper.nameOf<IAdvancedSecurityEntity>("Lock"),
                                    title: this.labels.Platform_Role_Security_Lock,
                                    getCellValue: i => this.renderPermissionCell(i, "lock"),
                                    columnWidth: 9,
                                },
                                {
                                    columnId: Helper.nameOf<IAdvancedSecurityEntity>("Unlock"),
                                    title: this.labels.Platform_Role_Security_Unlock,
                                    getCellValue: i => this.renderPermissionCell(i, "unlock"),
                                    columnWidth: 9,
                                },
                                {
                                    columnId: Helper.nameOf<IAdvancedSecurityEntity>("Share"),
                                    title: this.labels.Platform_Role_Security_Share,
                                    getCellValue: i => this.renderPermissionCell(i, "share"),
                                    columnWidth: 9,
                                },
                            ]}
                            data={this.state.gridData}
                            rowId={i => i.id.toString()}
                            onChange={i => this.setState({ gridData: i })}
                            onRowsRequest={i => this.handleRowsRequest(i)}
                            refreshToken={this.state.gridRefreshToken}
                            paging={{ pageSize: 11 }}
                        />
                    </GridItem>
                </GridContainer>

            </Card>
        );
    }

    private renderPermissionCell(permissions: IEntityPermissions, permissionName: PermissionName): JSX.Element
    {
        const permissionValue = this.getPermission(permissions, permissionName);
        return (
            <div
                className={this.cirlceClassNames.get(permissionValue) ?? ''}
                onClick={() => this.onPermissionIncrement(permissions, permissionName)}
            >
            </div>
        );
    }
}

export interface IProps
{
    roleId: string;
    canUpdateRole: boolean;
}

export interface IState
{
    gridData: IGridData<IEntityPermissions>;
    gridRefreshToken: string;
}

export type PermissionName = 'create' | 'read' | 'update' | 'delete' | 'lock' | 'unlock' | 'share';

export interface IEntityPermissions
{
    id: number;
    recursiveNodes: boolean;
    eventTypeId: number;
    eventTypeName: string;
    eventTypeDisplayName: string;
    create: number;
    read: number;
    update: number;
    delete: number;
    lock: number;
    unlock: number;
    append: number;
    appendTo: number;
    share: number;
}